import { type BibleSection } from './bible-utils';

/**
 * Shared color coding for Scripture sections
 * Based on the established colors in BiblePositionChart
 */

export interface SectionColors {
    light: string;
    dark: string;
    lightBold: string;
    darkBold: string;
}

export const SCRIPTURE_SECTION_COLORS: Record<BibleSection, SectionColors> = {
    pentateuch: {
        light: 'hsl(217, 15%, 78%)',    // Barely blue-tinted gray
        dark: 'hsl(217, 10%, 35%)',
        lightBold: 'hsl(217, 91%, 60%)', // blue-600
        darkBold: 'hsl(217, 91%, 65%)'   // blue-500
    },
    historical: {
        light: 'hsl(142, 13%, 76%)',    // Barely green-tinted gray
        dark: 'hsl(142, 8%, 33%)',
        lightBold: 'hsl(142, 76%, 36%)', // green-600
        darkBold: 'hsl(142, 71%, 45%)'   // green-500
    },
    wisdom: {
        light: 'hsl(270, 15%, 79%)',    // Barely purple-tinted gray
        dark: 'hsl(270, 10%, 36%)',
        lightBold: 'hsl(270, 91%, 65%)', // purple-500
        darkBold: 'hsl(270, 87%, 73%)'   // purple-400
    },
    majorProphets: {
        light: 'hsl(239, 15%, 78%)',    // Barely indigo-tinted gray
        dark: 'hsl(239, 10%, 35%)',
        lightBold: 'hsl(239, 84%, 67%)', // indigo-500
        darkBold: 'hsl(239, 84%, 73%)'   // indigo-400
    },
    minorProphets: {
        light: 'hsl(173, 15%, 77%)',    // Barely teal-tinted gray
        dark: 'hsl(173, 10%, 34%)',
        lightBold: 'hsl(173, 80%, 40%)', // teal-500
        darkBold: 'hsl(173, 78%, 56%)'   // teal-400
    },
    gospelsAndActs: {
        light: 'hsl(0, 15%, 78%)',      // Barely red-tinted gray
        dark: 'hsl(0, 10%, 36%)',
        lightBold: 'hsl(0, 84%, 60%)',   // red-500
        darkBold: 'hsl(0, 91%, 71%)'     // red-400
    },
    paulineEpistles: {
        light: 'hsl(27, 15%, 77%)',     // Barely orange-tinted gray
        dark: 'hsl(27, 10%, 35%)',
        lightBold: 'hsl(27, 96%, 61%)',  // orange-500
        darkBold: 'hsl(27, 96%, 65%)'    // orange-400
    },
    generalEpistles: {
        light: 'hsl(38, 15%, 77%)',     // Barely amber-tinted gray
        dark: 'hsl(38, 10%, 35%)',
        lightBold: 'hsl(38, 92%, 50%)',  // amber-500
        darkBold: 'hsl(38, 92%, 60%)'    // amber-400
    },
    revelation: {
        light: 'hsl(48, 17%, 79%)',     // Barely yellow-tinted gray
        dark: 'hsl(48, 12%, 36%)',
        lightBold: 'hsl(48, 96%, 53%)',  // yellow-500
        darkBold: 'hsl(48, 96%, 65%)'    // yellow-400
    }
};

/**
 * Shared utility to get the appropriate color for a Scripture section
 */
export function getSectionBackgroundColor(section: BibleSection, isDark: boolean, isCurrent: boolean = false): string {
    const colors = SCRIPTURE_SECTION_COLORS[section];
    if (isCurrent) {
        return isDark ? colors.darkBold : colors.lightBold;
    }
    return isDark ? colors.dark : colors.light;
}
